/**
 * Icon Abstraction Layer
 *
 * Centralizes all icons used in the application.
 * Currently uses @mui/icons-material, but can be easily swapped to another library.
 *
 * Usage:
 *   import { EditIcon, DeleteIcon, SearchIcon } from '@/components/icons';
 *   OR
 *   import { EditIcon, DeleteIcon, SearchIcon } from '../components/icons';
 *
 * To swap icon libraries in the future:
 *   1. Update the imports below
 *   2. Ensure the exported names match
 *   3. All consuming components will automatically use the new icons
 */

// Import from MUI Icons
import AccountCircleIconMui from '@mui/icons-material/AccountCircle';
import AddIconMui from '@mui/icons-material/Add';
import { default as ArrowBackIconMui, default as ArrowLeftIconMui } from '@mui/icons-material/ArrowBack';
import ArrowForwardIconMui from '@mui/icons-material/ArrowForward';
import BookmarkIconMui from '@mui/icons-material/Bookmark';
import CheckIconMui from '@mui/icons-material/Check';
import SuccessIconMui from '@mui/icons-material/CheckCircle';
import ChevronLeftIconMui from '@mui/icons-material/ChevronLeft';
import ChevronRightIconMui from '@mui/icons-material/ChevronRight';
import CloseIconMui from '@mui/icons-material/Close';
import DashboardIconMui from '@mui/icons-material/Dashboard';
import ArrowDropDownIconMui from '@mui/icons-material/ArrowDropDown';
import ContentCopyIconMui from '@mui/icons-material/ContentCopy';
import DateRangeIconMui from '@mui/icons-material/DateRange';
import DeleteIconMui from '@mui/icons-material/Delete';
import FileTextIconMui from '@mui/icons-material/Description'; // Alternative to Lucide FileText
import DownloadIconMui from '@mui/icons-material/Download';
import EditIconMui from '@mui/icons-material/Edit';
import ErrorIconMui from '@mui/icons-material/Error';
import FavoriteIconMui from '@mui/icons-material/Favorite';
import FilterIconMui from '@mui/icons-material/FilterList';
import HomeIconMui from '@mui/icons-material/Home';
import InfoIconMui from '@mui/icons-material/Info';
import KeyIconMui from '@mui/icons-material/Key';
import LockIconMui from '@mui/icons-material/Lock';
import LockOpenIconMui from '@mui/icons-material/LockOpen';
import LockOutlinedIconMui from '@mui/icons-material/LockOutlined';
import LoginIconMui from '@mui/icons-material/Login';
import LogoutIconMui from '@mui/icons-material/Logout';
import MenuIconMui from '@mui/icons-material/Menu';
import MoreVertIconMui from '@mui/icons-material/MoreVert';
import NotificationsIconMui from '@mui/icons-material/Notifications';
import PersonIconMui from '@mui/icons-material/Person';
import PlayIconMui from '@mui/icons-material/PlayArrow'; // Alternative to Lucide Play
import PlayCircleIconMui from '@mui/icons-material/PlayCircle';
import RefreshIconMui from '@mui/icons-material/Refresh';
import SchoolIconMui from '@mui/icons-material/School';
import SearchIconMui from '@mui/icons-material/Search';
import SearchOffIconMui from '@mui/icons-material/SearchOff';
import SettingsIconMui from '@mui/icons-material/Settings';
import ShareIconMui from '@mui/icons-material/Share';
import SortIconMui from '@mui/icons-material/Sort';
import UploadIconMui from '@mui/icons-material/Upload';
import VisibilityIconMui from '@mui/icons-material/Visibility';
import VisibilityOffIconMui from '@mui/icons-material/VisibilityOff';
import WarningIconMui from '@mui/icons-material/Warning';
import PersonAddIconMui from '@mui/icons-material/PersonAdd';
import PersonOffIconMui from '@mui/icons-material/PersonOff';
import EmailIconMui from '@mui/icons-material/Email';
import HelpOutlineIconMui from '@mui/icons-material/HelpOutline';
import BlockIconMui from '@mui/icons-material/Block';
import CheckCircleIconMui from '@mui/icons-material/CheckCircle';
import PendingIconMui from '@mui/icons-material/Pending';
import LockResetIconMui from '@mui/icons-material/LockReset';
import WorkspacesIconMui from '@mui/icons-material/Workspaces';
import LabelIconMui from '@mui/icons-material/Label';
import LibraryBooksIconMui from '@mui/icons-material/LibraryBooks';
import DragIndicatorIconMui from '@mui/icons-material/DragIndicator';
import CreateNewFolderIconMui from '@mui/icons-material/CreateNewFolder';
import GroupIconMui from '@mui/icons-material/Group';
import WorkspacePremiumIconMui from '@mui/icons-material/WorkspacePremium';
import QuizIconMui from '@mui/icons-material/Quiz';
import RadioButtonUncheckedIconMui from '@mui/icons-material/RadioButtonUnchecked';
import BarChartIconMui from '@mui/icons-material/BarChart';
import SendIconMui from '@mui/icons-material/Send';
import OpenInNewIconMui from '@mui/icons-material/OpenInNew';

// Export with consistent naming (remove "Mui" suffix)
export const EditIcon = EditIconMui;
export const ArrowDropDownIcon = ArrowDropDownIconMui;
export const ContentCopyIcon = ContentCopyIconMui;
export const DateRangeIcon = DateRangeIconMui;
export const DeleteIcon = DeleteIconMui;
export const SearchIcon = SearchIconMui;
export const SearchOffIcon = SearchOffIconMui;
export const LockIcon = LockIconMui;
export const LockOpenIcon = LockOpenIconMui;
export const LockOutlinedIcon = LockOutlinedIconMui;
export const AccountCircleIcon = AccountCircleIconMui;
export const VisibilityIcon = VisibilityIconMui;
export const VisibilityOffIcon = VisibilityOffIconMui;
export const KeyIcon = KeyIconMui;
export const ArrowBackIcon = ArrowBackIconMui;
export const ArrowForwardIcon = ArrowForwardIconMui;
export const PlayCircleIcon = PlayCircleIconMui;
export const DashboardIcon = DashboardIconMui;
export const SchoolIcon = SchoolIconMui;
export const SettingsIcon = SettingsIconMui;

// Icons to replace Lucide icons
export const ArrowLeftIcon = ArrowLeftIconMui;
export const ChevronLeftIcon = ChevronLeftIconMui;
export const ChevronRightIcon = ChevronRightIconMui;
export const FileTextIcon = FileTextIconMui;
export const PlayIcon = PlayIconMui;

// Common utility icons
export const AddIcon = AddIconMui;
export const CloseIcon = CloseIconMui;
export const MenuIcon = MenuIconMui;
export const MoreVertIcon = MoreVertIconMui;
export const CheckIcon = CheckIconMui;
export const InfoIcon = InfoIconMui;
export const WarningIcon = WarningIconMui;
export const ErrorIcon = ErrorIconMui;
export const SuccessIcon = SuccessIconMui;
export const DownloadIcon = DownloadIconMui;
export const UploadIcon = UploadIconMui;
export const RefreshIcon = RefreshIconMui;
export const FilterIcon = FilterIconMui;
export const SortIcon = SortIconMui;
export const FavoriteIcon = FavoriteIconMui;
export const ShareIcon = ShareIconMui;
export const BookmarkIcon = BookmarkIconMui;
export const NotificationsIcon = NotificationsIconMui;
export const HomeIcon = HomeIconMui;
export const PersonIcon = PersonIconMui;
export const LogoutIcon = LogoutIconMui;
export const LoginIcon = LoginIconMui;
export const PersonAddIcon = PersonAddIconMui;
export const PersonOffIcon = PersonOffIconMui;
export const EmailIcon = EmailIconMui;
export const HelpOutlineIcon = HelpOutlineIconMui;
export const BlockIcon = BlockIconMui;
export const CheckCircleIcon = CheckCircleIconMui;
export const PendingIcon = PendingIconMui;
export const LockResetIcon = LockResetIconMui;
export const WorkspacesIcon = WorkspacesIconMui;
export const GroupIcon = GroupIconMui;
export const LabelIcon = LabelIconMui;
export const LibraryBooksIcon = LibraryBooksIconMui;
export const DragIndicatorIcon = DragIndicatorIconMui;
export const CreateNewFolderIcon = CreateNewFolderIconMui;
export const CertificateIcon = WorkspacePremiumIconMui;
export const QuizIcon = QuizIconMui;
export const RadioButtonUncheckedIcon = RadioButtonUncheckedIconMui;
export const BarChartIcon = BarChartIconMui;
export const SendIcon = SendIconMui;
export const OpenInNewIcon = OpenInNewIconMui;

// Legacy exports for backward compatibility (can be removed after migration)
export { default as AccountCircle } from '@mui/icons-material/AccountCircle';
export { default as Key } from '@mui/icons-material/Key';
export { default as Visibility } from '@mui/icons-material/Visibility';
export { default as VisibilityOff } from '@mui/icons-material/VisibilityOff';
